import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CampoData from "@/components/common/CampoData";
import { PianoForm } from "@/pages/ContrattiPage";
import { eur } from "@/lib/formato";
import { erroreDi, giorno } from "@/lib/crm";
import { pesoFile } from "@/lib/documenti";
import { METODI_INCASSO, RATE, centesimiDaTesto, centesimiInApi, dataLunga, meseConsegna, oggiRoma } from "@/lib/soldi";

/* «Fatto fuori» (c36): un preventivo o un contratto firmato nato fuori dal gestionale. Si allega il file, si
   scrivono i pochi numeri che tengono veri i conti, e il cliente passa da solo nella colonna giusta (la decidono i
   fatti, non questa finestra). Il preventivo è una sola schermata; il contratto ha tre passi: il foglio, le rate
   (la stessa maschera dei Contratti, qui senza salvare) e il riepilogo con l'acconto.
   Lo apre la scheda cliente (`passo` vuoto: si sceglie qui) e il Kanban (`passo` già deciso dal gesto). */

const TIPI = [
  ["cucina", "Cucina"],
  ["divano", "Divano"],
  ["camera", "Camera"],
  ["armadio", "Armadio"],
  ["cabina", "Cabina armadio"],
  ["altro", "Altro"],
];

// L'id del contratto lo sceglie la finestra quando si apre: un secondo invio (la risposta si è persa) non ne fa un secondo.
export const nuovoId = () =>
  window.crypto?.randomUUID?.() ??
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r & 3) | 8).toString(16);
  });

const fraseErrore = (e) => (e?.response?.status === 413 ? "Il file è troppo grande: il massimo è 25 MB." : erroreDi(e));

function Campo({ id, etichetta, nota, children }) {
  return (
    <div className="grid content-start gap-inside">
      <Label htmlFor={id}>{etichetta}</Label>
      {children}
      {nota ? <p className="text-caption font-normal text-ink-2">{nota}</p> : null}
    </div>
  );
}

/* Un file solo, PDF o foto. `accept="image/*"` sul telefono offre anche la fotocamera. */
function SceltaFile({ etichetta, file, onFile, bloccato }) {
  const campo = useRef(null);
  const scelto = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // lo stesso file si può riscegliere
    if (f) onFile(f);
  };
  return (
    <div className="grid gap-inside">
      <span className="text-body font-medium text-ink-strong">{etichetta}</span>
      <input
        ref={campo}
        type="file"
        tabIndex={-1}
        accept="image/*,application/pdf"
        className="sr-only"
        aria-label={etichetta}
        onChange={scelto}
        disabled={bloccato}
        data-testid="fuori-file"
      />
      {file ? (
        <div className="flex items-center gap-group rounded-md border border-line px-group py-inside" data-testid="fuori-file-scelto">
          <div className="min-w-0 flex-1">
            <p className="truncate text-body text-ink-strong">{file.name}</p>
            <p className="text-caption font-normal tabular-nums text-ink-2">{pesoFile(file.size)}</p>
          </div>
          <Button type="button" variant="ghost" size="sm" disabled={bloccato} onClick={() => campo.current?.click()}>
            Cambia
          </Button>
        </div>
      ) : (
        <Button type="button" variant="secondary" className="justify-start" onClick={() => campo.current?.click()} data-testid="fuori-scegli-file">
          <Paperclip />
          Scegli un file o scatta una foto
        </Button>
      )}
      <p className="text-caption font-normal text-ink-2">PDF o foto, fino a 25 MB.</p>
    </div>
  );
}

/* Il corpo scorre, il piede no: i pulsanti restano sempre a portata di pollice. */
function Corpo({ children }) {
  return <div className="grid min-h-0 flex-1 content-start gap-card overflow-y-auto px-6 pb-6">{children}</div>;
}

/* Il piede: dice cosa manca (o l'errore del server) sopra i pulsanti. */
function Piede({ problema, errore, children }) {
  return (
    <div className="grid gap-inside border-t border-line bg-surface px-6 py-group max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]">
      {errore ? (
        <p role="alert" className="rounded-md bg-danger-bg px-group py-inside text-body text-danger-ink" data-testid="fuori-errore">
          {errore}
        </p>
      ) : problema ? (
        <p className="text-caption font-normal text-warning-ink" data-testid="fuori-manca">
          {problema}
        </p>
      ) : null}
      <div className="flex flex-col-reverse gap-inside sm:flex-row sm:justify-end">{children}</div>
    </div>
  );
}

function Scelta({ titolo, nota, onScegli, testId }) {
  return (
    <button
      type="button"
      onClick={onScegli}
      data-testid={testId}
      className="grid min-h-11 gap-micro rounded-lg border border-line-input bg-surface px-group py-group text-left transition-colors duration-150 ease-enter hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
    >
      <span className="text-body font-semibold text-ink-strong">{titolo}</span>
      <span className="text-body text-ink-2">{nota}</span>
    </button>
  );
}

export default function FattoFuori({ cliente, passo = null, onChiudi, onFatto }) {
  const [ctx, setCtx] = useState(null);
  const [erroreCtx, setErroreCtx] = useState(null);
  const [cosa, setCosa] = useState(passo);
  const [vista, setVista] = useState("foglio"); // del contratto: foglio · rate · conferma
  const [v, setV] = useState({ file: null, totale: "", data: oggiRoma(), firma: "", tipo: "cucina", titolo: "", numero: "" });
  const [chiusi, setChiusi] = useState(new Set());
  const [vendita, setVendita] = useState(null); // null = non ancora scelto · "nessuna" = è una vendita nuova · altrimenti l'id
  const richiesta = useRef(nuovoId());
  const [piano, setPiano] = useState(null);
  const [acconto, setAcconto] = useState({ si: false, data: oggiRoma(), metodo: "bonifico" });
  const [invio, setInvio] = useState(null); // null = fermo · 0-100 = sta caricando
  const [errore, setErrore] = useState(null);

  const leggi = () => {
    setErroreCtx(null);
    api
      .get(`/crm/clienti/${cliente.id}/fatto-fuori`)
      .then((r) => {
        setCtx(r.data);
        setChiusi(new Set(r.data.preventivi.map((p) => p.id))); // tutti spuntati: è il caso comune (D13)
        // Il passo già deciso vale solo se chi apre può farlo. Chi può una cosa sola (il venditore: il preventivo)
        // non ha una scelta da fare e va dritto al modulo: la scelta c'è solo per chi può tutte e due.
        const { puo } = r.data;
        setCosa((c) => ((c === "contratto" ? puo.contratto : c === "preventivo" ? puo.preventivo : false) ? c : puo.preventivo && !puo.contratto ? "preventivo" : null));
      })
      .catch((e) => setErroreCtx(fraseErrore(e)));
  };
  useEffect(leggi, [cliente.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const occupato = invio !== null;
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const oggi = oggiRoma();
  const dataOk = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d) && d <= oggi;
  const tot = centesimiDaTesto(v.totale);
  const nome = cliente.nome;

  const problemaComune = !v.file ? "Allega il file." : !(tot > 0) ? "Scrivi l'importo totale, IVA compresa." : null;
  const problemaPreventivo = problemaComune || (!dataOk(v.data) ? "Scegli il giorno in cui il cliente l'ha ricevuto (oggi o prima)." : null);
  const problemaFoglio =
    problemaComune ||
    (!dataOk(v.firma) ? "Scegli il giorno della firma (oggi o prima)." : null) ||
    (ctx?.vendite.length && vendita === null ? "Scegli se è una delle vendite di prima: altrimenti il venduto la conterebbe due volte." : null);
  const primo = piano?.righe.find((r) => r.tipo === "primo_acconto");
  const problemaConferma = acconto.si && !dataOk(acconto.data) ? "Scegli il giorno in cui hai incassato l'acconto." : null;

  async function manda(quale, dati) {
    setErrore(null);
    setInvio(0);
    try {
      const r = await api.post(`/crm/clienti/${cliente.id}/fatto-fuori/${quale}`, dati, {
        onUploadProgress: (ev) => ev.total && setInvio(Math.round((ev.loaded * 100) / ev.total)),
      });
      if (r.data?.gia_registrato) toast.success(`Era già registrato: ${nome} è a posto.`);
      else if (r.data?.acconto_non_segnato) toast.warning("Contratto registrato. L'acconto non è stato segnato: segnalo da Fatture e incassi.");
      else toast.success(quale === "preventivo" ? `Preventivo di ${nome} registrato` : `Contratto di ${nome} registrato`);
      setInvio(null);
      onFatto(quale, r.data);
    } catch (e) {
      setErrore(fraseErrore(e));
      setInvio(null); // il file scelto resta scelto: si corregge e si riprova
    }
  }

  const registraPreventivo = (e) => {
    e.preventDefault();
    if (problemaPreventivo || occupato) return;
    const d = new FormData();
    d.append("file", v.file);
    d.append("totale", centesimiInApi(tot));
    d.append("data", v.data);
    d.append("tipo_fornitura", v.tipo);
    if (v.titolo.trim()) d.append("titolo", v.titolo.trim());
    d.append("richiesta_id", richiesta.current);
    manda("preventivo", d);
  };

  const registraContratto = (e) => {
    e.preventDefault();
    if (problemaFoglio || problemaConferma || !piano || occupato) return;
    const d = new FormData();
    d.append("file", v.file);
    d.append("totale", centesimiInApi(tot));
    d.append("data_firma", v.firma);
    d.append("saldo_modalita", piano.saldo_modalita);
    d.append("rate", JSON.stringify(piano.righe));
    if (v.numero.trim()) d.append("numero_foglio", v.numero.trim());
    if (piano.consegna_voluta) d.append("consegna_voluta", piano.consegna_voluta);
    if (chiusi.size) d.append("preventivi_chiusi", JSON.stringify([...chiusi]));
    if (vendita && vendita !== "nessuna") d.append("vendita_storica", vendita);
    else if (ctx.vendite.length) d.append("vendita_nuova", "true"); // la scelta è stata fatta: è una vendita nuova
    d.append("richiesta_id", richiesta.current);
    d.append("acconto_incassato", acconto.si ? "true" : "false");
    if (acconto.si) {
      d.append("acconto_data", acconto.data);
      d.append("acconto_metodo", acconto.metodo);
    }
    manda("contratto", d);
  };

  const dellaScelta = !passo && ctx?.puo.preventivo && ctx?.puo.contratto;
  const testoInvio = (finale) => (invio === null ? finale : invio === 0 ? "Carico il file…" : invio < 100 ? `Carico il file… ${invio}%` : "Registro…");

  let titolo = "Fatto fuori";
  let descrizione = `Registra qui ciò che è nato fuori dal gestionale. ${nome} passa da solo nella colonna giusta.`;
  let corpo;

  if (erroreCtx) {
    corpo = (
      <Corpo>
        <div className="grid gap-group" data-testid="fuori-errore-lettura">
          <p role="alert" className="text-body text-ink-2">
            Non riesco a leggere i dati del cliente: {erroreCtx}
          </p>
          <Button type="button" variant="secondary" className="justify-self-start" onClick={leggi}>
            Riprova
          </Button>
        </div>
      </Corpo>
    );
  } else if (!ctx) {
    corpo = (
      <Corpo>
        <div className="grid gap-group" aria-busy="true" aria-label="Carico i dati">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      </Corpo>
    );
  } else if (!ctx.puo.preventivo && !ctx.puo.contratto) {
    corpo = (
      <Corpo>
        <p className="text-body text-ink-2">Il fatto fuori lo registrano il titolare e i venditori.</p>
      </Corpo>
    );
  } else if (!cosa) {
    titolo = "Che cosa hai fatto fuori?";
    corpo = (
      <Corpo>
        <Scelta testId="fuori-scelta-preventivo" titolo="Un preventivo" nota="Il cliente l'ha già ricevuto, su carta o da un altro programma." onScegli={() => setCosa("preventivo")} />
        <Scelta testId="fuori-scelta-contratto" titolo="Un contratto firmato" nota="Il cliente ha firmato fuori. Il contratto entra con le sue rate." onScegli={() => setCosa("contratto")} />
      </Corpo>
    );
  } else if (cosa === "preventivo") {
    titolo = "Un preventivo fatto fuori";
    descrizione = `Allega il file e scrivi l'importo. ${nome} passa da solo nella colonna giusta.`;
    corpo = (
      <form onSubmit={registraPreventivo} className="flex min-h-0 flex-1 flex-col" data-testid="fuori-form-preventivo">
        <Corpo>
          <SceltaFile etichetta="Il preventivo" file={v.file} onFile={(f) => setV((x) => ({ ...x, file: f }))} bloccato={occupato} />
          <Campo id="fuori-totale" etichetta="Importo totale, IVA compresa">
            <Input id="fuori-totale" inputMode="decimal" autoComplete="off" placeholder="22.000" value={v.totale} onChange={set("totale")} data-testid="fuori-totale" />
          </Campo>
          <Campo id="fuori-data" etichetta="Quando l'ha ricevuto il cliente">
            <CampoData id="fuori-data" value={v.data} max={oggi} onChange={set("data")} testId="fuori-data" />
          </Campo>
          <Campo id="fuori-tipo" etichetta="Che cos'è">
            <Select value={v.tipo} onValueChange={(t) => setV((x) => ({ ...x, tipo: t }))}>
              <SelectTrigger id="fuori-tipo" data-testid="fuori-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPI.map(([codice, etichetta]) => (
                  <SelectItem key={codice} value={codice}>
                    {etichetta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
          <Campo id="fuori-titolo" etichetta="Titolo (facoltativo)">
            <Input id="fuori-titolo" maxLength={200} placeholder="Es. Cucina Rossi, seconda versione" value={v.titolo} onChange={set("titolo")} data-testid="fuori-titolo" />
          </Campo>
        </Corpo>
        <Piede problema={problemaPreventivo} errore={errore}>
          <Button type="button" variant="secondary" disabled={occupato} onClick={dellaScelta ? () => setCosa(null) : onChiudi}>
            {dellaScelta ? "Indietro" : "Annulla"}
          </Button>
          <Button type="submit" disabled={Boolean(problemaPreventivo) || occupato} aria-busy={occupato} data-testid="fuori-registra-preventivo">
            {testoInvio("Registra il preventivo")}
          </Button>
        </Piede>
      </form>
    );
  } else if (vista === "foglio") {
    titolo = "Un contratto firmato fuori";
    descrizione = `Allega il firmato e scrivi i dati del foglio. Le rate si scrivono al passo dopo.`;
    corpo = (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!problemaFoglio) setVista("rate");
        }}
        className="flex min-h-0 flex-1 flex-col"
        data-testid="fuori-form-foglio"
      >
        <Corpo>
          <SceltaFile etichetta="Il contratto firmato" file={v.file} onFile={(f) => setV((x) => ({ ...x, file: f }))} bloccato={occupato} />
          <Campo id="fuori-firma" etichetta="Quando ha firmato">
            <CampoData id="fuori-firma" value={v.firma} max={oggi} onChange={set("firma")} testId="fuori-firma" />
          </Campo>
          <Campo id="fuori-totale" etichetta="Importo totale, IVA compresa">
            <Input
              id="fuori-totale"
              inputMode="decimal"
              autoComplete="off"
              placeholder="22.000"
              value={v.totale}
              onChange={(e) => {
                set("totale")(e);
                setPiano(null); // le rate fatte sul totale di prima non valgono più
              }}
              data-testid="fuori-totale"
            />
          </Campo>
          <Campo id="fuori-numero" etichetta="Numero sul foglio (facoltativo)" nota="Se il foglio ne ha uno, lo teniamo nelle note.">
            <Input id="fuori-numero" maxLength={60} value={v.numero} onChange={set("numero")} data-testid="fuori-numero" />
          </Campo>
          {ctx.preventivi.length ? (
            <fieldset className="grid gap-inside" data-testid="fuori-preventivi">
              <legend className="mb-inside text-body font-medium text-ink-strong">Questo contratto chiude questi preventivi</legend>
              {ctx.preventivi.map((p) => (
                <div key={p.id} className="flex items-start gap-group rounded-md border border-line px-group py-inside">
                  <Checkbox
                    id={`fuori-prev-${p.id}`}
                    className="mt-0.5"
                    checked={chiusi.has(p.id)}
                    onCheckedChange={(on) =>
                      setChiusi((s) => {
                        const n = new Set(s);
                        if (on) n.add(p.id);
                        else n.delete(p.id);
                        return n;
                      })
                    }
                    data-testid={`fuori-prev-${p.id}`}
                  />
                  <Label htmlFor={`fuori-prev-${p.id}`} className="grid min-w-0 flex-1 cursor-pointer gap-micro font-normal">
                    <span className="truncate text-body text-ink-strong">{p.titolo || p.numero || "Preventivo"}</span>
                    <span className="text-caption font-normal text-ink-2">
                      inviato {giorno(p.inviato_at)}
                      {p.totale ? ` · ${eur(p.totale, { decimali: 0 })}` : ""}
                    </span>
                  </Label>
                </div>
              ))}
              <p className="text-caption font-normal text-ink-2">Quelli senza spunta restano aperti.</p>
            </fieldset>
          ) : null}
          {ctx.vendite.length ? (
            <fieldset className="grid gap-inside" data-testid="fuori-vendite">
              <legend className="mb-inside text-body font-medium text-ink-strong">È una di queste vendite?</legend>
              <p className="text-caption font-normal text-ink-2">Sono le vendite di prima del gestionale, dai fogli Excel. Se è la stessa, il venduto la conta una volta sola.</p>
              <RadioGroup value={vendita ?? ""} onValueChange={setVendita}>
                {[{ id: "nessuna", testo: "No, è una vendita nuova" }, ...ctx.vendite.map((x) => ({ id: x.id, testo: `${giorno(x.data)} · ${eur(x.importo)}` }))].map((o) => (
                  <div key={o.id} className="flex min-h-11 items-center gap-group rounded-md border border-line px-group py-inside">
                    <RadioGroupItem id={`fuori-vend-${o.id}`} value={o.id} data-testid={`fuori-vend-${o.id}`} />
                    <Label htmlFor={`fuori-vend-${o.id}`} className="flex-1 cursor-pointer font-normal">
                      {o.testo}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </fieldset>
          ) : null}
        </Corpo>
        <Piede problema={problemaFoglio}>
          <Button type="button" variant="secondary" onClick={dellaScelta ? () => setCosa(null) : onChiudi}>
            {dellaScelta ? "Indietro" : "Annulla"}
          </Button>
          <Button type="submit" disabled={Boolean(problemaFoglio)} data-testid="fuori-avanti-rate">
            Avanti: le rate
          </Button>
        </Piede>
      </form>
    );
  } else if (vista === "rate") {
    titolo = "Le rate del contratto";
    descrizione = `Come paga ${nome}, come sul foglio. La somma deve fare ${eur(centesimiInApi(tot))}.`;
    corpo = (
      <Corpo>
        <PianoForm
          d={{ contratto: { totale: centesimiInApi(tot), consegna_voluta: piano?.consegna_voluta || null, saldo_modalita: piano?.saldo_modalita || null }, piano: piano?.righe || [] }}
          onSubmit={(p) => {
            setPiano(p);
            setVista("conferma");
          }}
          onAnnulla={() => setVista("foglio")}
          etichettaSalva="Avanti"
          etichettaAnnulla="Indietro"
        />
      </Corpo>
    );
  } else {
    titolo = "Controlla e registra";
    descrizione = `Dopo la registrazione ${nome} passa nella colonna giusta e il firmato resta nei suoi documenti.`;
    const vendutaScelta = ctx.vendite.find((x) => x.id === vendita);
    corpo = (
      <form onSubmit={registraContratto} className="flex min-h-0 flex-1 flex-col" data-testid="fuori-form-conferma">
        <Corpo>
          <dl className="grid gap-inside text-body">
            {[
              ["Firmato il", dataLunga(v.firma)],
              ["Totale", `${eur(centesimiInApi(tot))} IVA compresa`],
              ["Il file", v.file?.name],
              ["Consegna voluta", piano.consegna_voluta ? meseConsegna(piano.consegna_voluta) : null],
              ["Preventivi chiusi", chiusi.size ? String(chiusi.size) : null],
              ["Vendita di prima", vendutaScelta ? `${giorno(vendutaScelta.data)} · ${eur(vendutaScelta.importo)}, non conta due volte` : null],
            ]
              .filter(([, valore]) => valore)
              .map(([etichetta, valore]) => (
                <div key={etichetta} className="grid grid-cols-[8rem_minmax(0,1fr)] gap-inside">
                  <dt className="text-ink-2">{etichetta}</dt>
                  <dd className="break-words text-ink-strong">{valore}</dd>
                </div>
              ))}
          </dl>
          <ol className="grid gap-inside rounded-md border border-line px-group py-inside" aria-label="Le rate" data-testid="fuori-rate">
            {piano.righe.map((r) => (
              <li key={r.tipo} className="flex items-baseline justify-between gap-group text-body">
                <span className="min-w-0 text-ink-strong">
                  {r.causale || RATE[r.tipo]}
                  {r.scadenza ? <span className="text-ink-2"> · entro {dataLunga(r.scadenza)}</span> : null}
                </span>
                <span className="tabular-nums text-ink-strong">{eur(r.importo)}</span>
              </li>
            ))}
          </ol>
          {primo ? (
            <div className="grid gap-group rounded-md border border-line px-group py-group" data-testid="fuori-acconto">
              <div className="flex items-start gap-group">
                <Checkbox id="fuori-acconto-si" className="mt-0.5" checked={acconto.si} onCheckedChange={(on) => setAcconto((a) => ({ ...a, si: Boolean(on) }))} data-testid="fuori-acconto-si" />
                <Label htmlFor="fuori-acconto-si" className="grid cursor-pointer gap-micro font-normal">
                  <span className="text-body text-ink-strong">L'acconto l'ho già incassato (facoltativo)</span>
                  <span className="text-caption font-normal text-ink-2">Lo segno sulla rata «{primo.causale || RATE.primo_acconto}», {eur(primo.importo)}.</span>
                </Label>
              </div>
              {acconto.si ? (
                <div className="grid gap-group sm:grid-cols-2">
                  <Campo id="fuori-acconto-data" etichetta="Quando">
                    <CampoData id="fuori-acconto-data" value={acconto.data} max={oggi} onChange={(e) => setAcconto((a) => ({ ...a, data: e.target.value }))} testId="fuori-acconto-data" />
                  </Campo>
                  <Campo id="fuori-acconto-metodo" etichetta="Come ha pagato">
                    <Select value={acconto.metodo} onValueChange={(m) => setAcconto((a) => ({ ...a, metodo: m }))}>
                      <SelectTrigger id="fuori-acconto-metodo" data-testid="fuori-acconto-metodo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {METODI_INCASSO.map((m) => (
                          <SelectItem key={m.codice} value={m.codice}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Campo>
                </div>
              ) : null}
            </div>
          ) : null}
        </Corpo>
        <Piede problema={problemaConferma} errore={errore}>
          <Button type="button" variant="secondary" disabled={occupato} onClick={() => setVista("rate")}>
            Indietro
          </Button>
          <Button type="submit" disabled={Boolean(problemaConferma) || occupato} aria-busy={occupato} data-testid="fuori-registra-contratto">
            {testoInvio("Registra il contratto")}
          </Button>
        </Piede>
      </form>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !occupato && onChiudi()}>
      <DialogContent
        className="flex max-h-[92dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl max-sm:left-0 max-sm:top-0 max-sm:h-dvh max-sm:max-h-none max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0"
        data-testid="fuori-dialogo"
      >
        <DialogHeader className="px-6 pb-group pr-12 pt-6 text-left">
          <DialogTitle>{titolo}</DialogTitle>
          <DialogDescription>{descrizione}</DialogDescription>
        </DialogHeader>
        {corpo}
      </DialogContent>
    </Dialog>
  );
}
